/**
 * Client-side API helpers with AWS Amplify authentication for use in React components.
 */

'use client';

import { fetchAuthSession } from '@aws-amplify/auth';

interface FetchOptions extends RequestInit {
  skipAuthCheck?: boolean;
}

/**
 * Hook that provides an authenticated fetch function for client components.
 * Automatically injects AWS Amplify JWT token.
 */
export function useAuthenticatedFetch() {
  const fetchWithAuth = async (url: string, options?: FetchOptions): Promise<Response> => {
    // Get AWS Amplify token
    let token: string | undefined;
    try {
      const session = await fetchAuthSession();
      token = session.tokens?.accessToken?.toString();
    } catch (error) {
      console.error('Error fetching auth session:', error);
    }

    const headers = new Headers(options?.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 - redirect to sign-in
    if (response.status === 401 && !options?.skipAuthCheck) {
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }

    return response;
  };

  const fetchJSON = async <T = unknown>(url: string, options?: FetchOptions): Promise<T> => {
    const response = await fetchWithAuth(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }

    // 204 No Content is a successful response with an empty body.
    if (response.status === 204) {
      return undefined as T;
    }

    // Some misrouted/proxied requests return HTML (e.g. index.html) with 200 OK.
    // Parsing that as JSON throws a confusing SyntaxError ("Unexpected token '<'").
    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();

    if (!text) {
      return undefined as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch (e) {
      const snippet = text.slice(0, 200).replace(/\s+/g, ' ').trim();
      throw new Error(
        `Expected JSON but received non-JSON response from ${url} (content-type: ${contentType || 'unknown'}). ` +
          `First 200 chars: "${snippet}"`
      );
    }
  };

  return { fetchWithAuth, fetchJSON };
}
